/**
 * Плитка станції СЕС (дашборд портфелю, роль CTO/volodp). Концепція Прогноз/EMS:
 * скляна плитка на токенах, Urbanist, Lucide, догма, світло/темна.
 * Вища плитка, розподіл інформації за золотим перетином: ліворуч ілюстрація СЕС
 * (панель [+ батарея УЗЕ]), праворуч — блок показників (потужність+кільце,
 * вироблення, погода, інвертори, УЗЕ). Статус — обведена крапка біля назви.
 */
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { Sun, Cloud, CloudSun, CloudRain, CloudSnow, CloudFog, CloudLightning } from 'lucide-react';
import type { StationLive, StationWeather, WxKind } from './overviewTypes';
import { fmtMw, fmtMwh, fmtInt, fmtPct, loadPct, stationStatus, weatherCondition } from './overviewTypes';

// Іконка погоди за умовою (сонце — бурштин, решта — нейтральний dim).
function WxIcon({ kind }: { kind: WxKind }) {
  const map = { sun: Sun, partly: CloudSun, cloud: Cloud, fog: CloudFog, rain: CloudRain, snow: CloudSnow, storm: CloudLightning } as const;
  const I = map[kind];
  return <I size={11} style={{ color: kind === 'sun' ? 'var(--warn)' : 'var(--text-dim)' }} />;
}

// Статус — компактна обведена крапка: зелений=в роботі, жовтий=обмеження,
// червоний=аварія, сірий=не запущено/немає даних.
function StatusDot({ color, title }: { color: string; title: string }) {
  return (
    <span title={title} className="inline-flex items-center justify-center shrink-0"
      style={{ width: 14, height: 14, borderRadius: 999, border: `1.5px solid ${color}` }}>
      <span style={{ width: 6, height: 6, borderRadius: 999, background: color }} />
    </span>
  );
}

// Кільце %завантаження (потужність від встановленої) — зелене (генерація, догма).
function Ring({ pct, size = 52 }: { pct: number | null; size?: number }) {
  const r = (size - 6) / 2;
  const c = 2 * Math.PI * r;
  const off = c * (1 - (pct ?? 0) / 100);
  return (
    <svg width={size} height={size} style={{ flexShrink: 0 }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--line)" strokeWidth={4} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--ok)" strokeWidth={4}
        strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`} style={{ transition: 'stroke-dashoffset 0.5s ease' }} />
      <text x={size / 2} y={size / 2} textAnchor="middle" dominantBaseline="central"
        fontSize="11" fontWeight="600" fill="var(--text)" fontFamily="Urbanist, Inter, sans-serif">
        {pct == null ? '—' : Math.round(pct) + '%'}
      </text>
    </svg>
  );
}

// Сонячна панель (лінійна, без сонця) — трапеція з сіткою комірок на стійці.
function SolarPanel({ size = 96 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 96" fill="none" style={{ color: 'var(--text)' }}>
      <path d="M33 22 H67 Q70 22 71 25 L82 64 Q83.5 68 79 68 H21 Q16.5 68 18 64 L29 25 Q30 22 33 22 Z"
        stroke="currentColor" strokeWidth="2.4" strokeLinejoin="round" />
      <path d="M26 38 H74 M22 53 H78" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M44 22 L40 68 M56 22 L60 68" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M50 68 V86" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
      <path d="M38 86 H62" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  );
}

// Батарея УЗЕ (лінійна, із зеленими секціями заряду) — поряд із панеллю.
function BatteryUze({ h = 58 }: { h?: number }) {
  return (
    <svg width={h * 0.62} height={h} viewBox="0 0 40 64" fill="none">
      <rect x="15" y="3" width="10" height="5" rx="1.5" stroke="var(--text)" strokeWidth="2" />
      <rect x="7" y="8" width="26" height="52" rx="5" stroke="var(--text)" strokeWidth="2.2" />
      <rect x="12" y="45" width="16" height="9" rx="2" fill="var(--text)" opacity="0.9" />
      <rect x="12" y="33" width="16" height="9" rx="2" fill="var(--text)" opacity="0.55" />
      <rect x="12" y="21" width="16" height="9" rx="2" fill="var(--text)" opacity="0.28" />
    </svg>
  );
}

function Cell({ label, value, icon }: { label: string; value: string; icon?: React.ReactNode }) {
  return (
    <div className="min-w-0 text-right">
      <div className="flex items-center justify-end gap-1 text-[10px] mb-0.5 truncate" style={{ color: 'var(--text-dim)' }}>{icon}{label}</div>
      <div className="font-display text-[13px] leading-none truncate" style={{ color: 'var(--text)' }}>{value}</div>
    </div>
  );
}

export default function StationTile({ s, weather }: { s: StationLive; weather?: StationWeather }) {
  useTranslation();
  const st = stationStatus(s);
  const idLabel = s.eicW || s.assetId.slice(0, 8).toUpperCase();
  const flow = s.bess?.flow;
  const flowLabel = flow === 'discharge' ? i18n.t('ak_home_discharging') : flow === 'charge' ? i18n.t('ak_home_charging') : i18n.t('ak_home_idle');
  const wx = weatherCondition(weather);
  const uzeValue = s.bess
    ? `SoC ${fmtPct(s.bess.socPct)}% · ${flowLabel}`
    : (s.hasBess ? i18n.t('ak_home_no_data_2') : `${fmtMw(s.capacityKw)} ${i18n.t('ak_home_mw')}`);
  const uzeLabel = (s.bess || s.hasBess) ? i18n.t('ak_home_bess') : i18n.t('ak_home_capacity');

  return (
    <div className="tile p-5 flex flex-col gap-4" style={{ minHeight: 244 }}>
      {/* Заголовок: статус-крапка · назва · ID */}
      <div className="flex items-center gap-2">
        <StatusDot color={st.color} title={st.label} />
        <h3 className="font-display text-[15px] font-semibold leading-tight truncate flex-1" style={{ color: 'var(--text)' }} title={s.name}>{s.name}</h3>
        <span className="text-[10px] whitespace-nowrap shrink-0" style={{ color: 'var(--text-dim)' }}>ID: {idLabel}</span>
      </div>

      {/* Основне: ілюстрація (≈золотий менший бік) · показники (більший бік) */}
      <div className="flex items-center gap-5 flex-1">
        <div className="shrink-0 flex items-center justify-center" style={{ minWidth: s.hasBess ? 128 : 98 }}>
          <SolarPanel size={s.hasBess ? 84 : 96} />
          {s.hasBess && <BatteryUze h={56} />}
        </div>

        <div className="flex-1 min-w-0 flex flex-col gap-3.5">
          {/* Первинний показник: потужність + кільце */}
          <div className="flex items-center gap-3">
            <Ring pct={loadPct(s)} size={54} />
            <div className="min-w-0">
              <div className="text-[10.5px]" style={{ color: 'var(--text-muted)' }}>{i18n.t('ak_home_generation_now')}</div>
              <div className="flex items-baseline gap-1">
                <span className="font-display text-[26px] font-normal leading-none" style={{ color: 'var(--text)' }}>{fmtMw(s.pacKw)}</span>
                <span className="text-[11px]" style={{ color: 'var(--text-dim)' }}>{i18n.t('ak_home_mw')}</span>
              </div>
            </div>
          </div>

          {/* Вторинні показники 2×2 */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 pt-1" style={{ borderTop: '1px solid var(--line)' }}>
            <Cell label={i18n.t('ak_home_generated_today')} value={`${fmtMwh(s.dailyKwh)} ${i18n.t('ak_home_mwh')}`} />
            <Cell label={wx.label} value={weather?.tempC == null ? '—' : `${fmtPct(weather.tempC)}° · ${fmtPct(weather.cloudPct)}%`}
              icon={<WxIcon kind={wx.kind} />} />
            <Cell label={i18n.t('ak_home_inverters')} value={s.expectedInverters ? `${fmtInt(s.availableInverters)}/${fmtInt(s.expectedInverters)}` : '—'} />
            <Cell label={uzeLabel} value={uzeValue} />
          </div>
        </div>
      </div>
    </div>
  );
}
